import { useState, useEffect } from 'react';
import { fetchAssistantReflection, fetchConsoleReflection } from '../api';
import ConversationsBrowser from '../components/ConversationsBrowser';

// The four editable config tabs (System Prompt / Actions / Upsell / Classifier)
// were removed 2026-07-20: they edited Salesforce Squish_Config__c records that
// the live assistant never read (its prompt + tools live in squish-backend code).
// Conversations is the working tab (captured chat, escalate to case) and opens
// first; FOS prompt is a read-only reflection of what actually runs.
// See squish-docs/architecture/fos-llm-reflection-view.md.
// Chuck prompt is the same idea for the internal ~ console agent, whose prompt
// and tools live in squish-crm-backend lib/console-agent.js.
const TABS = [
  { id: 'conversations', label: 'Conversations' },
  { id: 'live',          label: 'FOS prompt' },
  { id: 'chuck',         label: 'Chuck prompt' },
];

const MONO = "'Fira Code', 'Consolas', monospace";

const PRE_STYLE = {
  width: '100%', maxHeight: 'calc(100vh - 260px)', minHeight: 320, overflow: 'auto', padding: 16, margin: 0, fontSize: 12.5,
  fontFamily: MONO,
  background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)',
  borderRadius: 10, lineHeight: 1.6, boxSizing: 'border-box', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

function Segmented({ options, value, onChange }) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 3, gap: 2 }}>
      {options.map(o => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          style={{
            padding: '5px 12px', fontSize: 12, fontWeight: o.id === value ? 600 : 400,
            background: o.id === value ? 'var(--accent)' : 'transparent',
            color: o.id === value ? '#fff' : 'var(--text3)',
            border: 'none', borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s',
          }}
        >{o.label}</button>
      ))}
    </div>
  );
}

function Provenance({ reflection, fallback }) {
  return (
    <span style={{ fontSize: 12, color: 'var(--text3)' }} title={reflection.note || ''}>
      Read-only · generated from{' '}
      <code style={{ fontFamily: MONO, fontSize: 11.5 }}>{reflection.source || fallback}</code>
    </span>
  );
}

function Loading({ what }) {
  return <div style={{ color: 'var(--text3)', fontSize: 14, padding: 40, textAlign: 'center' }}>Loading {what}…</div>;
}

function LoadError({ what, error }) {
  return (
    <div style={{ padding: '10px 14px', background: '#f871711a', border: '1px solid #f8717130', borderRadius: 8, color: '#f87171', fontSize: 13 }}>
      Couldn't load {what}: {error}
    </div>
  );
}

// Loads a reflection once per mount. A failed request renders as an error,
// never as an empty prompt — "couldn't load" must not read as "has no prompt".
function useReflection(fetcher) {
  const [state, setState] = useState({ data: null, loading: true, error: '' });
  useEffect(() => {
    let cancelled = false;
    fetcher()
      .then(data => { if (!cancelled) setState({ data, loading: false, error: '' }); })
      .catch(err => { if (!cancelled) setState({ data: null, loading: false, error: err.message }); });
    return () => { cancelled = true; };
  }, [fetcher]);
  return state;
}

export default function Assistant() {
  const [tab, setTab] = useState('conversations');

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1000, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px', color: 'var(--text)' }}>Squish Assistant</h1>
        <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>
          Captured chats from the front-of-site assistant, and the prompts it and the ~ console run.
        </p>
      </div>

      {/* Tabs */}
      <div className="m-scroll-x" style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 18px', fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
              background: 'none', border: 'none', cursor: 'pointer',
              color: tab === t.id ? 'var(--accent)' : 'var(--text3)',
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1, transition: 'all 0.15s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'live' ? <LivePrompt /> : tab === 'chuck' ? <ChuckPrompt /> : <ConversationsBrowser />}
    </div>
  );
}

// Read-only reflection of the LIVE assistant: its composed prompt (built from
// squish-backend code) plus the LLM changelog. Layout is prompt-first — one
// toolbar row (variant toggle + where the prompt comes from), the prompt, and
// the changelog folded away underneath so it never pushes the prompt off-screen.
function LivePrompt() {
  const { data: reflection, loading, error } = useReflection(fetchAssistantReflection);
  const [variantId, setVariantId] = useState(null);

  if (loading) return <Loading what="live prompt" />;
  if (error) return <LoadError what="the live prompt" error={error} />;

  const variants = reflection.variants || [];
  const variant = variants.find(v => v.id === variantId) || variants[0];
  const changelog = reflection.changelog || [];

  return (
    <div>
      {/* Toolbar: variant toggle on the left, provenance on the right */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        {variants.length > 1
          ? <Segmented options={variants} value={variant?.id} onChange={setVariantId} />
          : <span />}
        <Provenance reflection={reflection} fallback="squish-backend" />
      </div>

      <pre style={PRE_STYLE}>{variant?.prompt || '(no prompt)'}</pre>

      {changelog.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text)', userSelect: 'none' }}>
            Recent changes <span style={{ fontWeight: 400, color: 'var(--text3)' }}>({changelog.length})</span>
          </summary>
          <div style={{ marginTop: 10, borderLeft: '2px solid var(--border)', paddingLeft: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {changelog.map((c, i) => (
              <div key={i}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{c.title}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)', fontVariantNumeric: 'tabular-nums' }}>{c.date}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>{c.detail}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ─── Chuck (the ~ console agent) ─────────────────────────────────────────────
// Read-only reflection from GET /api/console/reflection. Chuck's system prompt
// is the same on every request, so instead of variants this tab splits into
// the prompt, the tool catalog (which is most of what Chuck can actually do),
// and the per-request view message that carries what the staffer has on screen.

// Where the effort level came from. A typo'd CONSOLE_EFFORT is silently
// ignored by the agent, so the panel says so instead of showing a bare value.
const EFFORT_SOURCE = {
  env: { text: 'from CONSOLE_EFFORT', warn: false },
  default: { text: 'default (CONSOLE_EFFORT unset)', warn: false },
  'default-invalid-env': { text: 'default: CONSOLE_EFFORT is not a valid level and is being ignored', warn: true },
};

function Fact({ label, value, hint, warn }) {
  return (
    <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
      {label}{' '}
      <code style={{ fontFamily: MONO, fontSize: 11.5, color: 'var(--text)' }}>{value}</code>
      {hint && <span style={{ color: warn ? 'var(--badge-yellow)' : 'var(--text3)', whiteSpace: 'normal' }}> · {hint}</span>}
    </span>
  );
}

function Badge({ children, tone = 'neutral', title }) {
  const color = tone === 'write' ? 'var(--badge-yellow)' : tone === 'role' ? 'var(--accent)' : 'var(--text3)';
  return (
    <span
      title={title}
      style={{
        fontSize: 10, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
        padding: '1px 6px', borderRadius: 4, background: 'var(--bg3)', color, whiteSpace: 'nowrap',
      }}
    >{children}</span>
  );
}

function ChuckPrompt() {
  const { data: r, loading, error } = useReflection(fetchConsoleReflection);
  const [view, setView] = useState('prompt');

  if (loading) return <Loading what="console prompt" />;
  if (error) return <LoadError what="the console prompt" error={error} />;

  const tools = r.tools || [];
  const writeCount = tools.filter(t => t.writes !== 'never').length;
  const effort = EFFORT_SOURCE[r.effortSource] || { text: r.effortSource, warn: false };
  const views = [
    { id: 'prompt', label: 'System prompt' },
    { id: 'tools',  label: `Tools (${tools.length})` },
    { id: 'view',   label: 'View message' },
  ];

  return (
    <div>
      {/* What the agent runs on — the knobs that are not in the prompt text */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginBottom: 12 }}>
        <Fact label="Model" value={r.model} />
        <Fact label="Effort" value={r.effort} hint={effort.text} warn={effort.warn} />
        {r.thinking && <Fact label="Thinking" value={`${r.thinking.type}, ${r.thinking.display}`} />}
        <Fact label="Max output" value={`${r.maxTokens} tokens`} />
        <Fact label="Tool calls / command" value={r.maxToolIterations} />
        <Fact label="History kept" value={`${r.maxTurns} turns`} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        <Segmented options={views} value={view} onChange={setView} />
        <Provenance reflection={r} fallback="squish-crm-backend" />
      </div>

      {view === 'prompt' && <pre style={PRE_STYLE}>{r.systemPrompt || '(no prompt)'}</pre>}

      {view === 'view' && (
        <>
          <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 8px' }}>
            Sent with every command after the staffer's message. The ids come from whatever CRM page they have open; the placeholder below stands in for them.
          </p>
          <pre style={PRE_STYLE}>{r.viewMessage || '(none)'}</pre>
        </>
      )}

      {view === 'tools' && (
        <div style={{ ...PRE_STYLE, padding: 0, fontFamily: 'inherit', whiteSpace: 'normal' }}>
          <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--text3)', borderBottom: '1px solid var(--row-border)' }}>
            {tools.length} tools · {writeCount} can change records, and each one asks the staffer to confirm first
          </div>
          {tools.map(t => (
            <ToolRow key={t.name} tool={t} fosWriteRoles={r.fosWriteRoles} />
          ))}
        </div>
      )}
    </div>
  );
}

function ToolRow({ tool, fosWriteRoles }) {
  // The [WRITE] prefix is for the model; the badge says it for people.
  const description = tool.description.replace(/^\[WRITE\]\s*/, '');
  const required = new Set(tool.required || []);
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--row-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
        <code style={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{tool.name}</code>
        {tool.writes === 'always' && <Badge tone="write" title="Changes records. Refuses until the staffer confirms the exact change.">Write</Badge>}
        {tool.writes === 'when-mutating' && (
          <Badge
            tone="write"
            title={`Reads run freely. Tools that change a customer's live account need confirmation${fosWriteRoles?.length ? ` and one of these roles: ${fosWriteRoles.join(', ')}` : ''}.`}
          >Write if mutating{fosWriteRoles?.length ? ` · ${fosWriteRoles.join(', ')}` : ''}</Badge>
        )}
        {tool.roles && <Badge tone="role" title="Only staff with these roles are offered this tool.">{tool.roles.join(', ')} only</Badge>}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text3)', lineHeight: 1.5 }}>{description}</div>
      {tool.params?.length > 0 && (
        <div style={{ marginTop: 4, fontFamily: MONO, fontSize: 11.5, color: 'var(--text3)' }}>
          {tool.params.map((p, i) => (
            <span key={p}>
              {i > 0 && ', '}
              <span style={{ color: required.has(p) ? 'var(--text2)' : 'var(--text3)' }}>{p}{required.has(p) ? '*' : ''}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
